import { message } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import * as React from 'react';
import { fileUploadOnS3 } from 'src/api/inquiry';
import { uploadImageToS3 } from 'src/api/salesorder';
import useS3Upload from 'src/hooks/useS3Upload';
import { useGenericState } from 'src/Library/UseGenericState';
import { getFileExtension } from 'src/utils/utils';
import uuid from 'uuid';
import { initialState } from './ExcelS3Upload.constants';
import {
  AttachmentUploadMapType,
  ExcelS3UploadInterfaceProps,
  GetS3LinksParamsType,
  PutFileToS3ParamsType,
  UploadMap,
} from './ExcelS3Upload.types';

export const useModel = (props: ExcelS3UploadInterfaceProps) => {
  const {
    onDataLoaded,
    sampleLinkDownload,
    moduleType,
    isS3LinkApiEnabled,
    uploadedFileS3Link,
    removeFile,
    headerText,
    fileType,
    customMessage,
    sampleLinkInfoText,
    sampleLink,
    classes,
    isMultiple,
    accept,
    showFileIcon = true,
    disableSampleLink,
    isLoading,
    isUploadToS3Enabled,
    onFilesUploaded,
  } = props;

  const [state, setState] = useGenericState<typeof initialState>(initialState);

  const { fileList, attachmentUploadMap, isUploading, isDownloading } = state;

  const initialPath = moduleType || 'rfq';

  const s3Upload = useS3Upload(initialPath);

  React.useEffect(() => {
    setState({
      attachmentUploadMap: {
        uploadMap: {},
        s3Links: [],
        uploading: false,
      },
    }, () => {
      if (isS3LinkApiEnabled) {
        getS3Links();
      } else if (fileList?.length && isUploadToS3Enabled) {
        onFilesUploaded({ ...attachmentUploadMap, uploading: true });
        handleFileData({ fileArray: fileList });
      } else if (fileList?.length) {
        onDataLoaded(fileList);
      }
    });
  }, [fileList]);

  React.useEffect(() => {
    const {
      uploadState: { isUploading, s3File, response },
    } = s3Upload;
    if (!s3Upload.uploadState.isUploading && s3File?.s3Link) {
      uploadedFileS3Link({
        s3UploadMap: [s3File.s3Link],
        fileName: s3File.name,
        isUploading: false,
      });
    }
  }, [s3Upload.uploadState.isUploading]);

  const getS3Links = () => {
    if (fileList?.length && !isMultiple) {
      fileList?.forEach((file) => {
        s3Upload.uploadToS3(file);
      });
    }
    uploadedFileS3Link({
      s3UploadMap: [],
      fileName: undefined,
      isUploading: !!fileList?.length,
    });
  };

  const handleFileChange = (fileChange: any) => {
    if (fileChange.file.status !== 'removed') {
      setState({
        fileList: isMultiple ? fileChange?.fileList : [fileChange?.file],
      });
    }
  };

  const handelRemoveFile = (e: any) => {
    if (isMultiple) {
      handleAttachmentRemove(e);
    } else {
      setState({
        fileList: [],
      });
    } 
    if (removeFile)
      removeFile();
  };

  const handleCustomRequest = ({ onSuccess, onError, file }) => {
    setTimeout(() => {
      onSuccess(null, file);
    }, 100);
  };

  const handleDownloadFile = async () => {
    if (isLoading || isDownloading) return;

    setState(
      { isDownloading: true },
      async () => {
        try {
          await Promise.resolve(sampleLinkDownload({}));
        } finally {
          setState({ isDownloading: false });
        }
      },
    );
  };

  const getUploadProgress = (
    progressEvent: any,
    uid: string,
    uploadMap: AttachmentUploadMapType
  ) => {
    const tempMap = uploadMap;
    if (progressEvent?.loaded && tempMap?.uploadMap?.[uid]?.uid) {
      const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
      tempMap.uploadMap[uid].percent = progress;
      setState({
        attachmentUploadMap: tempMap,
      });
    }
  };

  const setAttachmentUploadNewMap = (uploadMap: AttachmentUploadMapType, cb: () => void) => {
    setState(
      {
        attachmentUploadMap: uploadMap,
      },
      () => {
        cb();
      }
    );
  };

  const getS3UploadLink = async ({
    payload,
    file,
    uid,
  }: // uploadMap,
  GetS3LinksParamsType) => {
    const tempAttachmentUploadMap = attachmentUploadMap;
    if (tempAttachmentUploadMap?.uploadMap?.[uid]?.name) {
      const resFileUpload = await fileUploadOnS3(payload);
      if (resFileUpload.isSuccess) {
        await putFileToS3({
          uid,
          file: file?.originFileObj || file,
          post: resFileUpload?.data?.post,
          get: resFileUpload?.data?.get,
          extension: payload.extension,
          cb: getUploadProgress,
          // uploadMap,
        });
      }
    }
  };

  const putFileToS3 = async ({
    file,
    post,
    get,
    extension,
    cb,
    uid,
  }: // uploadMap,
  PutFileToS3ParamsType) => {
    const tempAttachmentUploadMap = attachmentUploadMap;
    const response = await uploadImageToS3(file, post, extension, cb, uid, tempAttachmentUploadMap);
    if (response.isSuccess && tempAttachmentUploadMap?.uploadMap?.[uid]?.name) {
      tempAttachmentUploadMap.uploadMap[uid].url = get;
      tempAttachmentUploadMap.uploadMap[uid].status = 'done';
      tempAttachmentUploadMap.uploadMap[uid].percent = 100;
      tempAttachmentUploadMap.s3Links.push(get);
      tempAttachmentUploadMap.uploading = !(
        Object.keys(tempAttachmentUploadMap?.uploadMap)?.length ===
        tempAttachmentUploadMap.s3Links?.length
      );
      setAttachmentUploadNewMap(tempAttachmentUploadMap, verifyAllAttachmentsAreUploaded);
    }
  };

  const verifyAllAttachmentsAreUploaded = (uploadMap?: AttachmentUploadMapType) => {
    const tempAttachmentUploadMap = uploadMap || attachmentUploadMap;
    const allFiles = Object.values(tempAttachmentUploadMap.uploadMap);
    const isUploading = allFiles?.filter((map) => !map.url)?.length;

    if (!isUploading) {
      setState({
        isUploading: false,
      });
      tempAttachmentUploadMap.uploading = false;
      onFilesUploaded(tempAttachmentUploadMap);
    }
  };

  const handleFileData = async ({ fileArray }) => {
    if (fileArray?.length) {
      const tempAttachmentUploadMap = attachmentUploadMap;
      // if (tempAttachmentUploadMap[inputKey]) {
      //   delete tempAttachmentUploadMap[inputKey];
      //   setState({
      //     attachmentUploadMap: tempAttachmentUploadMap,
      //   });
      // }
      (fileArray || [])?.forEach(async (file) => {
        if (
          Object.values(tempAttachmentUploadMap.uploadMap || {}).filter(
            (map) => map.name === file.name
          )?.length
        ) {
          return;
        }
        const uid = uuid();
        const map = {
          uid,
          name: file?.name,
          status: 'uploading',
          percent: 0,
          url: undefined,
        } as UploadMap;

        tempAttachmentUploadMap.uploadMap[uid] = map;

        const payload = {
          initialPath,
          extension: getFileExtension(map?.name),
          fileName: map?.name,
          document_type: file?.type,
        };
        await getS3UploadLink({ payload, file, uid });
      });
      setState({
        attachmentUploadMap: tempAttachmentUploadMap,
        isUploading: true,
      });
    } else {
      message.error('Upload a File');
    }
  };

  const getFileList = (): any => {
    if (isMultiple) {
      return Object.values(attachmentUploadMap?.uploadMap || {});
    }
    return fileList;
  };

  const handleAttachmentRemove = (e: any) => {
    const fileMap = attachmentUploadMap?.uploadMap?.[e?.uid];
    if (!fileMap?.status) {
      return;
    }
    const tempAttachmentUploadMap = attachmentUploadMap;
    const s3Links = attachmentUploadMap.s3Links as string[];
    tempAttachmentUploadMap.s3Links = s3Links?.filter(
      (link) => link !== tempAttachmentUploadMap?.uploadMap?.[e?.uid].url
    );
    delete tempAttachmentUploadMap?.uploadMap?.[e?.uid];
    if (tempAttachmentUploadMap?.uploading) {
      const allUploadingList = Object.values(tempAttachmentUploadMap.uploadMap).filter(
        (map) => map.status === 'uploading'
      );
      if (!allUploadingList?.length) {
        tempAttachmentUploadMap.uploading = false;
      }
    }
    setState(
      {
        attachmentUploadMap: tempAttachmentUploadMap,
      },
      () => {
        verifyAllAttachmentsAreUploaded(tempAttachmentUploadMap);
      }
    );
  };

  return {
    ...props,
    ...state,
    isDownloading: props.isDownloading ?? isDownloading,
    handleFileChange,
    handelRemoveFile,
    handleDownloadFile,
    handleCustomRequest,
    getFileList,
  };
};
